import isEqual from 'react-fast-compare';
import jsonPointer from 'json-pointer';
import copy from 'fast-copy';
import AbstractControl from './AbstractControl';
import setToObject from '../utils/setToObject';
import createRef from '../utils/createRef';
import toDataPath from '../utils/toDataPath';
import dictToObject from '../utils/dictToObject';
import getDict from '../utils/getDict';
import computeUIProps from '../utils/computeUIProps';
import getPointer from '../utils/getPointer';

export default class FormControl extends AbstractControl {
  constructor(props) {
    super(props);

    this.initSubscriptions([
      'aftervalidate',
      'beforevalidate',
      'blur',
      'change',
      'focus',
      'submit',
      'invalid',
      'valid',
      'validate',
      'validateFields',
    ]);

    this.actions = {
      clearErrors: this.clearErrors,
      clearValues: this.clearValues,
      focusFirstError: this.focusFirstError,
      getDefaultHandler: () => this.defaultHandler,
      getErrorMessage: this.getErrorMessage,
      handleReset: this.handleReset,
      handleSubmit: this.handleSubmit,
      handleValidate: this.handleValidate,
      removeError: this.removeError,
      removeValue: this.removeValue,
      reset: this.reset,
      setCurrentTouched: this.setCurrentTouched,
      setError: this.setError,
      setInitialValues: this.setInitialValues,
      getState: this.getState,
      setTouched: this.setTouched,
      setValue: this.setValue,
      submit: this.submit,
      validate: this.validate,
    };
  }

  withContext(context) {
    super.withContext(context);

    const { $submitted, errorsDict, getError, initialValues, schema, touched, values } = context;

    this.schema = schema;
    this.fields = this.computeFields(schema);
    this.touched = setToObject(touched);
    this.isValid = Object.keys(errorsDict).length === 0;
    this.isPristine = !$submitted && isEqual(initialValues, values);
    this.type = this.schema?.type;
    this.error = getError('');
    this.UIProps = computeUIProps({
      ...(String.prototype.toLowerCase(this.type) === 'object' ? this.schema?.['ui:props'] : {}),
      ...this.props,
    });

    const keyValues = Object.keys(values ?? {});
    const isRootValue = keyValues.length === 1 && keyValues.includes('');

    this.values = isRootValue ? values[''] : values;

    return this;
  }

  getProps = () => ({
    ...this.UIProps,
    ...this.defaultHandler,
  });

  getUserProps = () => ({
    ...this.UIProps,
  });

  get properties() {
    const isTouched = this.context.touched.size > 0;

    return {
      $dirty: !this.isPristine,
      $invalid: !this.isValid,
      $pristine: this.isPristine,
      $submitted: this.context.$submitted,
      $submitting: this.context.$submitting,
      $touched: isTouched,
      $untouched: !isTouched,
      $valid: this.isValid,
      compiledSchema: this.context.schema,
      defaultHandler: this.defaultHandler,
      defaultValues: this.context.defaultValues,
      error: this.error,
      errors: this.context.errors,
      fields: this.fields,
      initialValues: this.context.initialValues,
      touched: this.touched,
      values: this.values,
    };
  }

  get defaultHandler() {
    return {
      id: this.context.uuid,
      noValidate: true,
      onBlur: this.handleOnBlur,
      onFocus: this.handleOnFocus,
      onSubmit: this.handleSubmit,
    };
  }

  subscribeContext = () => {
    this.context.subscribe('aftervalidate', this.handleAfterValidateEvent);
    this.context.subscribe('aftervalidate', this.handleValidateEvent);
    this.context.subscribe('aftervalidate', this.handleValidateFieldsEvent);
    this.context.subscribe('beforevalidate', this.handleBeforeValidateEvent);
    this.context.subscribe('blur', this.handleBlurEvent);
    this.context.subscribe('change', this.handleChangeEvent);
    this.context.subscribe('focus', this.handleFocusEvent);
    this.context.subscribe('error', this.handleErrorChangeEvent);
  };

  unsubscribeContext = () => {
    this.context.unsubscribe('aftervalidate', this.handleAfterValidateEvent);
    this.context.unsubscribe('aftervalidate', this.handleValidateEvent);
    this.context.unsubscribe('aftervalidate', this.handleValidateFieldsEvent);
    this.context.unsubscribe('beforevalidate', this.handleBeforeValidateEvent);
    this.context.unsubscribe('blur', this.handleBlurEvent);
    this.context.unsubscribe('change', this.handleChangeEvent);
    this.context.unsubscribe('focus', this.handleFocusEvent);
    this.context.unsubscribe('error', this.handleErrorChangeEvent);
  };

  handleBlurEvent = ({ event }) => {
    this.trigger('blur', {
      ...this.ownContext,
      event,
    });
  };

  handleFocusEvent = ({ event }) => {
    this.trigger('focus', {
      ...this.ownContext,
      event,
    });
  };

  handleChangeEvent = (changedValues) => {
    this.trigger('change', {
      ...this.ownContext,
      changedValues,
    });
  };

  handleErrorChangeEvent = (changedErrors) => {
    const { errorsDict } = this.context;
    let errorHandler = 'valid';

    if (Object.keys(errorsDict).length > 0) {
      errorHandler = 'invalid';
    }

    this.trigger(errorHandler, {
      ...this.ownContext,
      changedErrors,
    });
  };

  handleAfterValidateEvent = (data, errors) =>
    this.triggerAsync('aftervalidate', {
      ...this.ownContext,
      values: data,
      errors,
    });

  handleBeforeValidateEvent = (data) =>
    this.triggerAsync('beforevalidate', {
      ...this.ownContext,
      values: data,
    });

  handleValidateEvent = async (data) => {
    const errorMessages = await this.triggerAsync(
      'validate',
      data,
      this.context.formStateRef.current
    );

    return errorMessages.map((errorMessage) => {
      if (errorMessage) {
        return {
          errors: {
            '': errorMessage,
          },
        };
      }

      return null;
    });
  };

  getUuid = () => this.context.uuid;

  setTouched = (...args) => {
    const { setTouched } = this.context;

    setTouched(...args);
  };

  setCurrentTouched = (...args) => {
    const { setCurrentTouched } = this.context;

    setCurrentTouched(...args);
  };

  setValue = ($ref, value) => {
    const dataPath = toDataPath(createRef($ref));

    this.context.setValues((values) => {
      if (dataPath === '') {
        return { '': value };
      }

      const nextValues = dictToObject(copy(values)) ?? {};

      try {
        jsonPointer.set(nextValues, dataPath, value);
      } catch (e) {
        // try setting a new property on the parent path
        const propertyName = dataPath.split('/').pop();
        const parentDataPath = dataPath.slice(0, -propertyName.length - 1);

        if (
          parentDataPath != null &&
          typeof getPointer(nextValues, parentDataPath) === 'undefined'
        ) {
          jsonPointer.set(nextValues, parentDataPath, { [propertyName]: value });
        }
      }

      return getDict(nextValues);
    });
  };

  removeValue = ($ref) => {
    const dataPath = toDataPath(createRef($ref));

    this.context.setValues((values) => {
      if (dataPath === '') {
        return { '': undefined };
      }

      const nextValues = dictToObject(copy(values)) ?? {};

      jsonPointer.set(nextValues, dataPath, undefined);

      return getDict(nextValues);
    });
  };

  setInitialValues = (...args) => {
    const { setInitialValues } = this.context;

    return setInitialValues(...args);
  };

  getState = () => {
    const { getState } = this.context;

    return getState();
  };

  clearValues = () => {
    this.context.setValues(() => null);
  };

  setError = ($ref, message) => {
    const dataPath = toDataPath(createRef($ref));

    this.context.setErrors((errors) => {
      const nextErrors = dictToObject(copy(errors));

      jsonPointer.set(nextErrors, dataPath, message);

      return getDict(nextErrors);
    });
  };

  removeError = ($ref) => {
    const dataPath = toDataPath(createRef($ref));

    this.context.setErrors((errors) => {
      const nextErrors = dictToObject(copy(errors));

      jsonPointer.set(nextErrors, dataPath, undefined);

      return getDict(nextErrors);
    });
  };

  clearErrors = (...args) => {
    const { clearErrors } = this.context;

    return clearErrors(...args);
  };

  reset = () => {
    const { reset } = this.context;

    return reset();
  };

  focusFirstError = () => this.context.focusFirstError();

  submit = async () => {
    this.context.setSubmitting();

    const values = await this.context.validate();

    if (!this.isValid || values === false) {
      this.context.focusFirstError();

      this.context.setSubmitting(false);

      return false;
    }

    await this.triggerAsync('submit', {
      ...this.ownContext,
      values,
    });

    this.context.setSubmitting(false);

    return true;
  };

  validate = () => {
    const { validate } = this.context;

    return validate();
  };

  handleValidate = (e) => {
    if (e) e.preventDefault();

    return this.validate();
  };

  handleSubmit = (e) => {
    if (e) e.preventDefault();

    return this.submit();
  };

  handleReset = (e) => {
    if (e) e.preventDefault();

    return this.reset();
  };

  handleOnBlur = (e) => {
    e.persist();

    this.context.trigger('blur', { event: e });
  };

  handleOnFocus = (e) => {
    e.persist();

    this.context.trigger('focus', { event: e });
  };
}
